import { useState } from 'react';
import { Mail, Copy, Check, Trash2 } from 'lucide-react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface InviteParentSectionProps {
  playerId: Id<"players">;
}

export function InviteParentSection({ playerId }: InviteParentSectionProps) {
  const [email, setEmail] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const codes = useQuery(api.director.inviteCodes.listForPlayer, { playerId });
  const generate = useMutation(api.director.inviteCodes.generate);
  const revoke = useMutation(api.director.inviteCodes.revoke);

  const appUrl = window.location.origin;

  async function handleGenerate() {
    setIsGenerating(true);
    try {
      const result = await generate({ playerId, parentEmail: email.trim() || undefined });
      setGeneratedCode(result.code);
      setEmail('');
    } finally {
      setIsGenerating(false);
    }
  }

  function copyCode(code: string) {
    const joinUrl = `${appUrl}/parent/join?code=${code}`;
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const activeCodes = codes ?? [];

  return (
    <div className="bg-card rounded-lg shadow">
      <div className="p-4 border-b flex items-center gap-2">
        <Mail className="w-5 h-5 text-muted-foreground" />
        <h2 className="font-semibold">Invite Parent</h2>
      </div>
      <div className="p-4 space-y-4">
        <p className="text-sm text-muted-foreground">
          Generate an invite code so a parent can link their account to this player.
        </p>

        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Parent email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1"
          />
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? 'Generating…' : 'Generate'}
          </Button>
        </div>

        {generatedCode && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Code generated!</p>
            <div className="flex items-center gap-2">
              <code className="text-lg font-bold tracking-widest text-primary bg-card px-3 py-1 rounded border border-border">
                {generatedCode}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyCode(generatedCode)}
                className="flex items-center gap-1"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {email
                ? 'Email sent. Parent can also use the code at /parent/join.'
                : 'Share this code or link with the parent.'}
            </p>
          </div>
        )}

        {activeCodes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active codes</p>
            {activeCodes.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3">
                  <code className="font-mono text-sm font-bold tracking-wider text-foreground">{c.code}</code>
                  {c.parentEmail && (
                    <span className="text-xs text-muted-foreground">{c.parentEmail}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyCode(c.code)}
                    className="h-7 px-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => revoke({ inviteCodeId: c.id })}
                    className="h-7 px-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
